<template>
  <div
    class="clara-panel is-flex is-flex-direction-column has-background-white overflow-hidden"
    :class="panelClasses"
    :style="panelStyle"
  >
    <header
      class="is-flex is-align-items-flex-start has-gap-8 has-border-bottom-light has-user-select-none p-4"
    >
      <div class="is-flex is-flex-direction-column is-flex-grow-1 has-gap-8">
        <div class="is-flex is-align-items-center has-gap-8">
          <img
            v-if="!isFullScreen"
            ref="resizeHandle"
            :src="claraResizeIcon"
            alt=""
            class="clara-panel-resize is-absolute t-0 l-0 has-width-20 has-height-20"
            :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_RESIZE_HANDLE"
            @pointerdown="onResizeStart"
          />
          <img :src="claraStarIcon" alt="" class="has-width-20 has-height-20" />
          <span class="clara-panel-title is-size-3 has-text-weight-medium whitespace-no-wrap">
            {{ $t('ai.clara-for-planners.panel-title') }}
          </span>
          <TagView type="is-primary is-light" size="is-small" class="m-l-10">
            {{ $t('ai.clara-for-planners.beta') }}
          </TagView>
        </div>

        <ClaraForPlannersContextHeader :jurisdiction="selectedJurisdiction" :plan="selectedPlan" />
      </div>

      <ButtonView
        type="is-ghost"
        class="clara-panel-minimize is-flex-shrink-0 p-0"
        :aria-label="$t('ai.clara-for-planners.minimize-aria')"
        @click="onMinimizeClick"
      >
        <img :src="claraDownIcon" alt="" />
      </ButtonView>
    </header>

    <div class="clara-panel-body is-flex-grow-1 is-overflow-y-auto p-4">
      <ClaraConversationView
        ref="conversationView"
        :conversation="conversation"
        :show-header="false"
        :show-model-selector="false"
        :origin="origin"
        @on-answer="onAnswer"
      >
        <template #message-header>
          <ClaraForPlannersWelcome
            class="m-b-10"
            :welcome-message="welcomeMessage"
            :questions="seededQuestions"
            @question-selected="onSeededQuestionSelected"
          />
        </template>

        <template #answer-footer="{ message }">
          <ClaraForPlannersEscalationCard v-if="message.isUpgradeEscalation" class="m-t-16" />
          <ButtonView
            v-if="canTryAgain(message)"
            type="is-primary is-light"
            class="m-t-16"
            :data-e2e="AICypressAttribute.buttons.CLARA_FOR_PLANNERS_TRY_AGAIN"
            @click="onTryAgainClick"
          >
            <span class="has-text-weight-semibold">{{ $t('ai.try-again') }}</span>
          </ButtonView>
        </template>
      </ClaraConversationView>
    </div>

    <ClaraForPlannersFooter @submit="onMessageSubmit" />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import ClaraConversationView from '@/modules/ai/components/stateful/ClaraConversationView.vue';
import ClaraForPlannersFooter from '@/modules/ai/components/presentational/clara-for-planners/ClaraForPlannersFooter.vue';
import ClaraForPlannersContextHeader from '@/modules/ai/components/presentational/clara-for-planners/ClaraForPlannersContextHeader.vue';
import ClaraForPlannersWelcome from '@/modules/ai/components/presentational/clara-for-planners/ClaraForPlannersWelcome.vue';
import ClaraForPlannersEscalationCard from '@/modules/ai/components/stateful/clara-for-planners/ClaraForPlannersEscalationCard.vue';
import TagView from '@/modules/core/ui/components/wrappers/TagView.vue';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import { ClaraMessageObject } from '@/modules/ai/models/ClaraMessageObject';
import { ClaraForPlannersQuestionAskedEventTracking } from '@/modules/ai/models/segmentevents/ClaraForPlannersQuestionAskedEventTracking';
import { ClaraForPlannersResponseGeneratedEventTracking } from '@/modules/ai/models/segmentevents/ClaraForPlannersResponseGeneratedEventTracking';
import ClaraService from '@/modules/ai/services/ClaraService';
import Logger from '@/modules/core/app/helpers/Logger';
import { ClaraForPlannersSeededQuestion } from '@/modules/ai/constants/ClaraForPlannersSeededQuestion';
import { OpenAIModel } from '@/modules/ai/constants/OpenAIModel';
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import { ClaraForPlannersResizeOrigin } from '@/modules/ai/interfaces/ClaraForPlannersResizeOrigin';
import { ClaraConversationOrigin } from '@/modules/ai/constants/ClaraConversationOrigin';
import { getters } from '@/modules/core/app/helpers/store';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import DeviceService from '@/modules/core/ui/services/DeviceService';
import { CurrentEstate } from '@/modules/estate/models/CurrentEstate';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { VaultBillingPlanFormFactory } from '@/modules/estate/models/VaultBillingPlanFormFactory';
import claraDownIcon from '@/assets/illustrations/clara-down.svg';
import claraStarIcon from '@/assets/illustrations/clara-star-2.svg';
import claraResizeIcon from '@/assets/illustrations/clara-playground-planning-platform.svg';

export default defineComponent({
  name: 'ClaraForPlannersPanel',
  emits: ['minimize'],
  components: {
    ClaraConversationView,
    ClaraForPlannersFooter,
    ClaraForPlannersContextHeader,
    ClaraForPlannersWelcome,
    ClaraForPlannersEscalationCard,
    TagView,
    ButtonView
  },
  props: {
    conversation: {
      type: ClaraConversationObject,
      required: true
    },
    sessionId: {
      type: String as PropType<string>,
      required: true
    }
  },
  static: {
    AICypressAttribute,
    claraDownIcon,
    claraStarIcon,
    claraResizeIcon,
    origin: ClaraConversationOrigin.PLANNER
  },
  data(): {
    panelWidth: number;
    panelHeight: number | null;
    minPanelWidth: number;
    minPanelHeight: number;
    viewportMargin: number;
    resizeOrigin: ClaraForPlannersResizeOrigin | null;
    resizePointerId: number | null;
    resizeFrameId: number | null;
    pendingResizePoint: { x: number; y: number } | null;
    selectedJurisdiction: string | null;
    selectedPlan: string | null;
  } {
    return {
      panelWidth: 420,
      panelHeight: null,
      minPanelWidth: 360,
      minPanelHeight: 400,
      viewportMargin: 48,
      resizeOrigin: null,
      resizePointerId: null,
      resizeFrameId: null,
      pendingResizePoint: null,
      selectedJurisdiction: null,
      selectedPlan: null
    };
  },
  computed: {
    isFullScreen(): boolean {
      return DeviceService.isMobile();
    },
    panelClasses(): string[] {
      return this.isFullScreen
        ? ['is-full-fixed']
        : ['has-border-light', 'is-radius-large', 'b-24', 'r-24'];
    },
    panelStyle(): Record<string, string> {
      if (this.isFullScreen) {
        return {};
      }

      return {
        width: `${this.panelWidth}px`,
        height: this.panelHeight === null ? 'auto' : `${this.panelHeight}px`
      };
    },
    isConversationEmpty(): boolean {
      return !this.conversation.messages || this.conversation.messages.length === 0;
    },
    seededQuestions(): string[] {
      return this.isConversationEmpty ? ClaraForPlannersSeededQuestion.getQuestions() : [];
    },
    welcomeMessage(): string {
      const firstName: string | undefined = getters.auth.getUser()?.name?.split(' ')[0];

      return this.$t('ai.clara-for-planners.welcome', { name: firstName });
    }
  },
  methods: {
    onMinimizeClick(): void {
      this.$emit('minimize');
    },
    onResizeStart(event: PointerEvent): void {
      event.preventDefault();

      this.resizeOrigin = {
        x: event.clientX,
        y: event.clientY,
        width: this.panelWidth,
        height: this.panelHeight ?? this.minPanelHeight
      };
      this.resizePointerId = event.pointerId;

      // Capture keeps pointerup on the handle when the drag ends outside the window,
      // so the move listener always gets removed.
      (event.target as Element).setPointerCapture?.(event.pointerId);

      DocumentUIService.addEventListener('pointermove', this.onResizeMove);
      DocumentUIService.addEventListener('pointerup', this.onResizeEnd);
      DocumentUIService.addEventListener('pointercancel', this.onResizeEnd);
    },
    onResizeMove(event: PointerEvent): void {
      if (!this.resizeOrigin) {
        return;
      }

      this.pendingResizePoint = { x: event.clientX, y: event.clientY };

      // One write per frame keeps a drag from re-rendering the conversation on every move.
      if (this.resizeFrameId !== null) {
        return;
      }

      this.resizeFrameId = requestAnimationFrame(this.applyResize);
    },
    applyResize(): void {
      this.resizeFrameId = null;

      if (!this.resizeOrigin || !this.pendingResizePoint) {
        return;
      }

      const deltaX: number = this.resizeOrigin.x - this.pendingResizePoint.x;
      const deltaY: number = this.resizeOrigin.y - this.pendingResizePoint.y;

      this.panelWidth = this.clamp(
        this.resizeOrigin.width + deltaX,
        this.minPanelWidth,
        DocumentUIService.getViewportWidth() - this.viewportMargin
      );
      this.panelHeight = this.clamp(
        this.resizeOrigin.height + deltaY,
        this.minPanelHeight,
        DocumentUIService.getViewportHeight() - this.viewportMargin
      );
    },
    onResizeEnd(): void {
      if (this.resizeFrameId !== null) {
        cancelAnimationFrame(this.resizeFrameId);
        this.resizeFrameId = null;
      }

      this.releaseResizePointer();

      this.resizeOrigin = null;
      this.pendingResizePoint = null;

      DocumentUIService.removeEventListener('pointermove', this.onResizeMove);
      DocumentUIService.removeEventListener('pointerup', this.onResizeEnd);
      DocumentUIService.removeEventListener('pointercancel', this.onResizeEnd);
    },
    releaseResizePointer(): void {
      const handle: Element | undefined = this.$refs.resizeHandle as Element | undefined;

      if (this.resizePointerId === null) {
        return;
      }

      if (handle?.hasPointerCapture?.(this.resizePointerId)) {
        handle.releasePointerCapture(this.resizePointerId);
      }

      this.resizePointerId = null;
    },
    clamp(value: number, min: number, max: number): number {
      return Math.min(Math.max(value, min), max);
    },
    onAnswer(conversation: ClaraConversationObject, responseTimeMs: number): void {
      this.scrollToBottom();

      const message: ClaraMessageObject | undefined = conversation.latestAnswer;

      ClaraForPlannersResponseGeneratedEventTracking.dispatch(
        getters.auth.getUser(),
        this.sessionId,
        message,
        responseTimeMs
      );

      if (!message?.createsSupportTicket) {
        return;
      }

      ClaraService.requestSupportTicket(conversation, message).catch((error: unknown) =>
        Logger.error('Failed to send the Clara conversation to support:', error)
      );
    },
    onSeededQuestionSelected(question: string): void {
      this.onMessageSubmit(question, true);
    },
    canTryAgain(message: ClaraMessageObject): boolean {
      return (
        message.canRetrySystemError &&
        !!message.identifier &&
        message.identifier === this.conversation.latestMessage?.identifier
      );
    },
    onTryAgainClick(): void {
      this.conversation.discardLatestAnswer();

      (this.$refs.conversationView as { onReloadQuestion(): void } | undefined)?.onReloadQuestion();
    },
    onMessageSubmit(message: string, isSeededQuestion: boolean = false): void {
      this.conversation.addUserMessage(message);
      const firstMessage: ClaraMessageObject | undefined = this.conversation.messages?.[0];
      if (firstMessage && !firstMessage.modelName) {
        firstMessage.modelName = OpenAIModel.GPT_5_6_TERRA.key;
      }

      ClaraForPlannersQuestionAskedEventTracking.dispatch(
        getters.auth.getUser(),
        this.sessionId,
        this.conversation,
        isSeededQuestion
      );

      (this.$refs.conversationView as { askQuestion(): void } | undefined)?.askQuestion();
      this.scrollToBottom();
    },
    scrollToBottom(): void {
      (this.$refs.conversationView as { scrollToBottom(): void } | undefined)?.scrollToBottom();
    },
    setContextFromCurrentEstate(): void {
      const estate: CurrentEstate | null | undefined = getters.auth.getUser()?.currentEstate;
      const planId: BillingPlanId | undefined = estate?.plan?.planId;

      this.selectedJurisdiction = estate?.jurisdictionName || estate?.jurisdiction?.name || null;
      this.selectedPlan = planId
        ? VaultBillingPlanFormFactory.build(planId)?.computedName ?? null
        : null;
    }
  },
  created(): void {
    this.setContextFromCurrentEstate();
  },
  mounted(): void {
    this.panelHeight = this.clamp(
      (this.$el as HTMLElement).offsetHeight,
      this.minPanelHeight,
      DocumentUIService.getViewportHeight() - this.viewportMargin
    );
  },
  beforeUnmount(): void {
    this.onResizeEnd();
  }
});
</script>

<style scoped>
.clara-panel {
  position: fixed;
  box-shadow: 0px 0 28px 0 var(--ce-grey-light);
  z-index: var(--ce-z-index-clara-panel);
}

.clara-panel-resize {
  cursor: url('@/assets/illustrations/clara-expand-arrow.svg') 6 6, nwse-resize;
}

.clara-panel-minimize.button.is-ghost:hover {
  background-color: transparent;
}

.clara-panel-title {
  background: linear-gradient(90deg, var(--ce-primary), var(--ce-tertiary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.clara-panel-body {
  min-height: 0;
}

.clara-panel-body :deep(.question-input) {
  display: none;
}

.clara-panel-body :deep(.clara-answer) {
  padding-bottom: 0 !important;
}
</style>
